import { useMutation, useQuery } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  billingCreditSchema,
  cancelSubscriptionResultSchema,
  checkoutResultSchema,
  paiementListSchema,
  stripeSessionSchema,
  type BillingCredit,
  type CancelSubscriptionResult,
  type CheckoutInput,
  type CheckoutResult,
  type PaiementList,
  type StripeSession,
} from '../schemas';
import { useToken } from './use-token';

/**
 * Choisit une formule. Selon l'état de l'abonnement, le serveur répond par
 * une caisse Stripe à suivre, un changement déjà appliqué, ou une
 * rétrogradation datée au prochain renouvellement.
 */
export function useCreateCheckout() {
  const api = useApi();

  return useMutation({
    mutationFn: (input: CheckoutInput) =>
      api.post<CheckoutResult>('/billing/checkout', input, { schema: checkoutResultSchema }),
  });
}

/** Abandonne une rétrogradation programmée : la formule en cours se renouvelle. */
export function useCancelScheduledChange() {
  const api = useApi();

  return useMutation({
    mutationFn: () => api.post<void>('/billing/changement/annuler'),
  });
}

/** Avoir Stripe restant sur le compte, en euros (0 si aucun). */
export function useBillingCredit() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.billing.credit(),
    queryFn: () => api.get<BillingCredit>('/billing/credit', { schema: billingCreditSchema }),
    enabled: token !== null,
  });
}

/** Portail Stripe : factures, moyen de paiement, résiliation en libre-service. */
export function useCreateBillingPortal() {
  const api = useApi();

  return useMutation({
    mutationFn: () =>
      api.post<StripeSession>('/billing/portal', undefined, { schema: stripeSessionSchema }),
  });
}

/**
 * Ce que verrait le cabinet s'il résiliait maintenant : date de fin d'accès
 * et avoir rendu, sans rien changer côté Stripe. `enabled: false` : on ne
 * calcule ça qu'à l'ouverture de la modale de confirmation, pas à chaque
 * chargement de la page.
 */
export function usePreviewCancelSubscription() {
  const api = useApi();

  return useQuery({
    queryKey: queryKeys.billing.cancelPreview(),
    queryFn: () =>
      api.get<CancelSubscriptionResult>('/billing/annulation/apercu', {
        schema: cancelSubscriptionResultSchema,
      }),
    enabled: false,
  });
}

/** Résilie l'abonnement en cours, avec un mois de préavis et l'avoir associé. */
export function useCancelSubscription() {
  const api = useApi();

  return useMutation({
    mutationFn: () =>
      api.post<CancelSubscriptionResult>('/billing/annulation', undefined, {
        schema: cancelSubscriptionResultSchema,
      }),
  });
}

/** Historique de facturation Stripe du cabinet connecté. */
export function usePayments() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.billing.invoices(),
    queryFn: () => api.get<PaiementList>('/billing/invoices', { schema: paiementListSchema }),
    enabled: token !== null,
  });
}
